import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, afterNextRender, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { HdAnnotationStore } from './hd-annotation-store';
import { buildFreehandPathD, hitTestAnnotation, simplifyPoints, translateAnnotation } from './hd-annotation-geometry';
import { HdAnnotation, HdPoint } from './hd-annotation.models';
import { HdChartButtonSize } from '../chart-tabs/hd-chart-shortcut-bar.component';
import { SharedModule } from '@shared/shared.module';

interface TextEditState {
  at: HdPoint;
  value: string;
  editingId: string | null;
  /** Host-relatieve schermpositie + font-grootte van de HTML-input-editor. */
  left: number;
  top: number;
  fontPx: number;
}

@Component({
  selector: 'hd-chart-annotation-layer',
  imports: [CdkDrag, CdkDragHandle, SharedModule, TranslocoModule],
  templateUrl: './hd-chart-annotation-layer.component.html',
  styleUrl: './hd-chart-annotation-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartAnnotationLayerComponent {
  readonly buttonSize = input<HdChartButtonSize>('xs');

  protected readonly store = inject(HdAnnotationStore);
  private readonly svgRef = viewChild.required<ElementRef<SVGSVGElement>>('annoSvg');
  private readonly textInputRef = viewChild<ElementRef<HTMLInputElement>>('annoTextInput');
  private readonly toolbarDrag = viewChild(CdkDrag);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Render-transform die opgeslagen chart-viewBox-coördinaten op de overlay
   * mapt, gelijk aan de chart-svg-CTM. Hierdoor vallen annotaties exact op de
   * chart én kan er ook buiten de chart (over de planeetgrids) getekend worden.
   */
  protected readonly gTransform = signal('');

  private cachedChartSvg: SVGSVGElement | null = null;

  constructor() {
    // Tekst-editor betrouwbaar focussen zodra de <input> daadwerkelijk in de
    // DOM staat (de viewChild-signal update na de render — i.t.t. queueMicrotask
    // dat vóór de render kon vuren waardoor de focus stilletjes mislukte).
    effect(() => {
      const input = this.textInputRef()?.nativeElement;
      if (input && this.textEdit()) {
        input.focus();
        input.select();
      }
    });

    // De overlay spant de hele chart-stack; de render-transform volgt de
    // chart-svg-CTM. Herbereken na de eerste render, wanneer de teken-modus
    // aangaat, en bij elke resize/layout-wijziging.
    afterNextRender(() => this.recomputeTransform());
    effect(() => {
      if (this.active()) {
        queueMicrotask(() => this.recomputeTransform());
      }
    });
    const ro = new ResizeObserver(() => this.recomputeTransform());
    ro.observe(this.hostEl.nativeElement);
    const stack = this.hostEl.nativeElement.closest('.chart-stack');
    if (stack) {
      ro.observe(stack);
    }
    this.destroyRef.onDestroy(() => ro.disconnect());
  }

  /** viewBox van de bodygraph-svg — moet identiek zijn aan `bodygraph-full.svg`. */
  protected readonly viewBox = '0 0 1182 1772';

  protected readonly annotations = this.store.annotations;
  protected readonly active = this.store.active;

  protected readonly palette = ['#111827', '#e11d48', '#2563eb', '#16a34a', '#f59e0b'];

  private readonly toolbarKeyboardStep = 8;

  private readonly drawingPoints = signal<HdPoint[] | null>(null);
  protected readonly draftArrow = signal<{ from: HdPoint; to: HdPoint } | null>(null);
  protected readonly textEdit = signal<TextEditState | null>(null);

  /** Standaard tekstgrootte in viewBox-eenheden. */
  private readonly textFontSize = 42;

  private erasing = false;

  /** Drempel in viewBox-eenheden voor hit-test van gom én verplaats. */
  private readonly hitThreshold = 8;

  private moveCtx: { id: string; start: HdPoint; original: HdAnnotation } | null = null;

  private eraseAt(p: HdPoint): void {
    // Bovenste (laatst getekende) treffer eerst.
    const list = this.store.annotations();
    for (let i = list.length - 1; i >= 0; i--) {
      if (hitTestAnnotation(p, list[i], this.hitThreshold)) {
        this.store.removeRaw(list[i].id);
        return;
      }
    }
  }

  /** Lokaliseert (en cachet) de geïnjecteerde chart-svg binnen dezelfde chart-stack. */
  private chartSvg(): SVGSVGElement | null {
    if (this.cachedChartSvg?.isConnected) {
      return this.cachedChartSvg;
    }
    const stack = this.hostEl.nativeElement.closest('.chart-stack');
    this.cachedChartSvg = stack?.querySelector<SVGSVGElement>('hd-bodygraph-chart svg') ?? null;
    return this.cachedChartSvg;
  }

  /**
   * Houdt {@link gTransform} gelijk aan de chart-svg-CTM, uitgedrukt in de
   * coördinaten van de overlay-svg. Geen chart-svg (bv. in tests) → identiteit,
   * zodat de overlay in zijn eigen pixelruimte werkt.
   */
  private recomputeTransform(): void {
    const chart = this.chartSvg();
    const overlay = this.svgRef().nativeElement;
    const chartCtm = chart?.getScreenCTM();
    const overlayCtm = overlay.getScreenCTM();
    if (!chartCtm || !overlayCtm) {
      return;
    }
    const m = overlayCtm.inverse().multiply(chartCtm);
    this.gTransform.set(`matrix(${m.a} ${m.b} ${m.c} ${m.d} ${m.e} ${m.f})`);
  }

  /**
   * Scherm-pixel → chart-viewBox-coördinaat via de chart-svg-CTM. Valt terug op
   * de overlay-svg wanneer er (nog) geen chart-svg is, zodat input en render
   * consistent blijven.
   */
  protected toViewBox(ev: PointerEvent | MouseEvent): HdPoint {
    const mapper = this.chartSvg() ?? this.svgRef().nativeElement;
    const ctm = mapper.getScreenCTM();
    if (!ctm) {
      return { x: 0, y: 0 };
    }
    const pt = new DOMPoint(ev.clientX, ev.clientY).matrixTransform(ctm.inverse());
    return { x: pt.x, y: pt.y };
  }

  /**
   * Host-relatieve schermpositie + font-grootte voor de tekst-editor-input,
   * afgeleid van de chart-svg-CTM zodat de editor exact verschijnt waar de
   * tekst (op `at`) zal renderen.
   */
  private buildTextPlacement(at: HdPoint): { left: number; top: number; fontPx: number } {
    const ctm = this.chartSvg()?.getScreenCTM();
    const hostRect = this.hostEl.nativeElement.getBoundingClientRect();
    if (!ctm) {
      return { left: 0, top: 0, fontPx: 20 };
    }
    const p = new DOMPoint(at.x, at.y).matrixTransform(ctm);
    const scale = Math.hypot(ctm.a, ctm.b) || 1;
    return { left: p.x - hostRect.left, top: p.y - hostRect.top, fontPx: this.textFontSize * scale };
  }

  /** Padstring voor een opgeslagen vrije-lijn-annotatie. */
  protected pathD(a: HdAnnotation): string {
    return a.kind === 'free' ? buildFreehandPathD(a.points) : '';
  }

  /** Live padstring tijdens het tekenen. */
  protected readonly draftPathD = computed(() => {
    const pts = this.drawingPoints();
    return pts ? buildFreehandPathD(pts) : '';
  });

  protected onPointerDown(ev: PointerEvent): void {
    // Render-transform vers houden zodat de net-getekende streek correct mapt.
    this.recomputeTransform();
    if (this.store.tool() === 'text') {
      // Commit any open editor first, then open a new one at the clicked point.
      this.commitText();
      const at = this.toViewBox(ev);
      this.textEdit.set({ at, value: '', editingId: null, ...this.buildTextPlacement(at) });
      return;
    }
    if (this.store.tool() === 'move') {
      const p = this.toViewBox(ev);
      const list = this.store.annotations();
      for (let i = list.length - 1; i >= 0; i--) {
        if (hitTestAnnotation(p, list[i], this.hitThreshold)) {
          (ev.target as Element).setPointerCapture?.(ev.pointerId);
          this.store.selectedId.set(list[i].id);
          this.store.beginChange();             // hele sleep = één undo-stap
          this.moveCtx = { id: list[i].id, start: p, original: list[i] };
          return;
        }
      }
      this.store.selectedId.set(null);
      return;
    }
    if (this.store.tool() === 'eraser') {
      (ev.target as Element).setPointerCapture?.(ev.pointerId);
      this.store.beginChange();      // het hele veeg-gebaar = één undo-stap
      this.erasing = true;
      this.eraseAt(this.toViewBox(ev));
      return;
    }
    if (this.store.tool() === 'arrow') {
      (ev.target as Element).setPointerCapture?.(ev.pointerId);
      const p = this.toViewBox(ev);
      this.draftArrow.set({ from: p, to: p });
      return;
    }
    if (this.store.tool() !== 'pen') {
      return;
    }
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    this.drawingPoints.set([this.toViewBox(ev)]);
  }

  protected onPointerMove(ev: PointerEvent): void {
    if (this.store.tool() === 'move' && this.moveCtx) {
      const p = this.toViewBox(ev);
      const dx = p.x - this.moveCtx.start.x;
      const dy = p.y - this.moveCtx.start.y;
      this.store.replaceRaw(this.moveCtx.id, translateAnnotation(this.moveCtx.original, dx, dy));
      return;
    }
    if (this.store.tool() === 'eraser' && this.erasing) {
      this.eraseAt(this.toViewBox(ev));
      return;
    }
    if (this.store.tool() === 'arrow' && this.draftArrow()) {
      const from = this.draftArrow()!.from;
      this.draftArrow.set({ from, to: this.toViewBox(ev) });
      return;
    }
    if (this.store.tool() !== 'pen' || !this.drawingPoints()) {
      return;
    }
    this.drawingPoints.update(pts => [...(pts ?? []), this.toViewBox(ev)]);
  }

  protected onPointerUp(): void {
    if (this.moveCtx) {
      this.moveCtx = null;
      return;
    }
    if (this.erasing) {
      this.erasing = false;
      return;
    }
    const arrow = this.draftArrow();
    if (arrow) {
      this.draftArrow.set(null);
      const dist = Math.hypot(arrow.to.x - arrow.from.x, arrow.to.y - arrow.from.y);
      if (dist >= 4) {
        this.store.add({
          id: this.store.nextId(),
          kind: 'arrow',
          color: this.store.color(),
          width: this.store.width(),
          opacity: this.store.opacity(),
          from: arrow.from,
          to: arrow.to,
        });
      }
      return;
    }
    const pts = this.drawingPoints();
    this.drawingPoints.set(null);
    if (this.store.tool() !== 'pen' || !pts || pts.length < 2) {
      return;
    }
    const ann: HdAnnotation = {
      id: this.store.nextId(),
      kind: 'free',
      color: this.store.color(),
      width: this.store.width(),
      opacity: this.store.opacity(),
      points: simplifyPoints(pts, 2),
    };
    this.store.add(ann);
  }

  /** Dubbelklik op bestaande tekst → bewerken (ongeacht de actieve tool). */
  protected onTextDblClick(a: HdAnnotation): void {
    if (a.kind !== 'text') {
      return;
    }
    this.textEdit.set({ at: a.at, value: a.text, editingId: a.id, ...this.buildTextPlacement(a.at) });
  }

  protected onTextInput(value: string): void {
    this.textEdit.update(s => (s ? { ...s, value } : s));
  }

  protected onToolbarDragHandleKeydown(event: KeyboardEvent): void {
    const movement = this.toolbarKeyboardMovement(event.key);
    if (!movement) {
      return;
    }

    const drag = this.toolbarDrag();
    const boundary = this.hostEl.nativeElement.closest<HTMLElement>('.chart-stack');
    if (!drag || !boundary) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const toolbarRect = drag.getRootElement().getBoundingClientRect();
    const boundaryRect = boundary.getBoundingClientRect();
    const deltaX = this.clampKeyboardDelta(
      movement.x,
      boundaryRect.left - toolbarRect.left,
      boundaryRect.right - toolbarRect.right,
    );
    const deltaY = this.clampKeyboardDelta(
      movement.y,
      boundaryRect.top - toolbarRect.top,
      boundaryRect.bottom - toolbarRect.bottom,
    );
    const currentPosition = drag.getFreeDragPosition();
    drag.setFreeDragPosition({ x: currentPosition.x + deltaX, y: currentPosition.y + deltaY });
  }

  private toolbarKeyboardMovement(key: string): HdPoint | null {
    switch (key) {
      case 'ArrowLeft': return { x: -this.toolbarKeyboardStep, y: 0 };
      case 'ArrowRight': return { x: this.toolbarKeyboardStep, y: 0 };
      case 'ArrowUp': return { x: 0, y: -this.toolbarKeyboardStep };
      case 'ArrowDown': return { x: 0, y: this.toolbarKeyboardStep };
      default: return null;
    }
  }

  private clampKeyboardDelta(requested: number, minimum: number, maximum: number): number {
    if (minimum > maximum) {
      return 0;
    }
    return Math.min(maximum, Math.max(minimum, requested));
  }

  @HostListener('document:keydown', ['$event'])
  protected onKeyDown(ev: KeyboardEvent): void {
    // Autofill- en andere synthetische keydown-events hebben soms geen key (Sentry-crash).
    if (!ev.key) {
      return;
    }
    // Niet vuren tijdens typen (incl. de tekst-editor) of met modifiers behalve Ctrl+Z.
    const target = ev.target as HTMLElement | null;
    const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'z') {
      if (this.store.active()) {
        ev.preventDefault();
        this.store.undo();
      }
      return;
    }
    if (typing) {
      return;
    }
    if (ev.shiftKey && ev.key.toLowerCase() === 'c') {
      if (this.store.active()) {
        ev.preventDefault();
        this.store.clear();
      }
      return;
    }
    if (ev.altKey || ev.ctrlKey || ev.metaKey) {
      return;
    }
    switch (ev.key.toLowerCase()) {
      case 'p': this.store.setTool('pen'); break;
      case 'a': this.store.setTool('arrow'); break;
      case 't': this.store.setTool('text'); break;
      case 'e': this.store.setTool('eraser'); break;
      case 'v': this.store.setTool('move'); break;
      case 'escape':
        // Tool-uit krijgt voorrang op de hover-ontdooi (spec §6/§13).
        if (this.store.active()) {
          ev.preventDefault();
          ev.stopPropagation();
          this.commitText();
          this.store.setTool('none');
        }
        break;
      default: return;
    }
    if ('patevn'.includes(ev.key.toLowerCase())) {
      ev.preventDefault();
    }
  }

  protected commitText(): void {
    const edit = this.textEdit();
    this.textEdit.set(null);
    if (!edit) {
      return;
    }
    const text = edit.value.trim();
    if (edit.editingId) {
      // Bewerken: leeg → verwijderen, anders vervangen (één undo-stap).
      this.store.beginChange();
      if (!text) {
        this.store.removeRaw(edit.editingId);
      } else {
        const existing = this.store.annotations().find(a => a.id === edit.editingId);
        if (existing && existing.kind === 'text') {
          this.store.replaceRaw(edit.editingId, { ...existing, text });
        }
      }
      return;
    }
    if (!text) {
      return;
    }
    this.store.add({
      id: this.store.nextId(),
      kind: 'text',
      color: this.store.color(),
      width: this.store.width(),
      opacity: this.store.opacity(),
      at: edit.at,
      text,
      fontSize: this.textFontSize,
    });
  }
}
